import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  addRowToTable,
  extractColumnDefinitions,
  validateRowData,
  ColumnFieldDefinition,
} from '@/services/tableRowService';
import { DynamicFormField } from './formFieldMapper';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useViewerStore } from '@/stores/viewer/viewer-store';
import { useToast } from '@/hooks/use-toast';

interface AddTableRowModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableComponentId: string;
}

/**
 * Modal for adding rows to table components
 * Dynamically generates form fields based on table column configuration
 */
export function AddTableRowModal({ isOpen, onClose, tableComponentId }: AddTableRowModalProps) {
  // Try to get component from both editor and viewer stores
  const editorStore = useEditorStore();
  const viewerStore = useViewerStore();
  const { toast } = useToast();
  const [columns, setColumns] = useState<ColumnFieldDefinition[]>([]);
  const [tableName, setTableName] = useState<string>('Table');

  // Create form with react-hook-form
  const form = useForm<Record<string, unknown>>({
    defaultValues: {},
  });

  // Extract column definitions when modal opens or table changes
  useEffect(() => {
    if (isOpen && tableComponentId) {
      // Try editor store first, then viewer store
      let tableComponent = editorStore.getComponent?.(tableComponentId);
      if (!tableComponent) {
        tableComponent = viewerStore.getComponentById?.(tableComponentId);
      }

      console.log('📋 [AddTableRowModal] Looking for table component:', {
        tableComponentId,
        foundInEditor: !!editorStore.getComponent?.(tableComponentId),
        foundInViewer: !!viewerStore.getComponentById?.(tableComponentId),
        tableComponent: tableComponent ? 'found' : 'not found',
      });

      if (tableComponent) {
        const columnDefs = extractColumnDefinitions(tableComponent);
        setColumns(columnDefs);

        // Set table name for display
        const name = (tableComponent.properties.title as string) || tableComponent.name || 'Table';
        setTableName(name);

        // Set default values for the form
        const defaultValues: Record<string, unknown> = {};
        columnDefs.forEach((col) => {
          if (col.defaultValue !== undefined) {
            defaultValues[col.name] = col.defaultValue;
          } else if (col.type === 'date') {
            // Default to today's date
            defaultValues[col.name] = new Date().toISOString().split('T')[0];
          } else if (col.type === 'select' && col.options && col.options.length > 0) {
            // Default to first option
            defaultValues[col.name] = col.options[0].value;
            // Set color if status field
            if (col.name.toLowerCase().includes('status') && col.options[0].color) {
              defaultValues.statusColor = col.options[0].color;
            }
          } else {
            defaultValues[col.name] = '';
          }
        });

        form.reset(defaultValues);
      } else {
        console.warn('📋 [AddTableRowModal] Table component not found:', tableComponentId);
      }
    }
  }, [isOpen, tableComponentId, editorStore, viewerStore, form]);

  const onSubmit = (data: Record<string, unknown>) => {
    // Validate the data
    const validation = validateRowData(data, columns);
    if (!validation.valid) {
      toast({
        title: 'Validation Error',
        description: validation.errors.join(', '),
        variant: 'destructive',
      });
      return;
    }

    // Add the row to the table
    const success = addRowToTable(tableComponentId, data);

    if (success) {
      form.reset();
      onClose();

      // Show toast after closing to avoid React warning
      setTimeout(() => {
        toast({
          title: 'Success',
          description: 'Row added successfully',
        });
      }, 100);
    } else {
      toast({
        title: 'Error',
        description: 'Failed to add row to table',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Row</DialogTitle>
          <DialogDescription>
            Add a new entry to {tableName}. Fill in the fields below.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 py-4 px-6">
              {columns.map((column) => (
                <DynamicFormField
                  key={column.name}
                  column={column}
                  control={form.control}
                  fieldName={column.name as never}
                />
              ))}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Adding...' : 'Add Row'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
